import type { TmsInvoiceRow } from '@numeo/types-main-web-app'
import { create } from 'zustand'
import { fetchLoadReadiness, fetchWorklistRows } from '../../api/worklistApi'
import { amazonRowsOf } from './cardResults'

interface FreshRowsStore {
    /** The rows read again after their paperwork changed, by load id: a card draws these in place of the row the chat drew. */
    rows: Record<string, TmsInvoiceRow>
    put: (row: TmsInvoiceRow) => void
}

export const useFreshRows = create<FreshRowsStore>((set) => ({
    rows: {},
    put: (row) => {
        if (!row.load_id) return
        const loadId = row.load_id
        set((state) => ({ rows: { ...state.rows, [loadId]: row } }))
    },
}))

/** The row a card draws: the load's row as it was read again since, else the row the chat drew. */
export const useFreshRow = (row: TmsInvoiceRow): TmsInvoiceRow => {
    const fresh = useFreshRows((state) => (row.load_id ? state.rows[row.load_id] : undefined))
    return fresh ?? row
}

/**
 * Reads one load's row again after its paperwork changed: first through the worklist's Ready and needs-documents parts, the same reader
 * the Agent page's panel uses; when the load has left them (say, it was just invoiced), through its readiness, read for that one load.
 * The chat row takes the new row in place; an invoiced load's row reads as invoiced. Nothing is sent to the agent. A failed read leaves
 * the row as it was.
 */
export async function refreshLoadRow(loadId: string, loadNumber: string): Promise<void> {
    try {
        const pages = await Promise.all(
            (['ready', 'needs_documents'] as const).map((part) => fetchWorklistRows(part, { page: 1, limit: 50, search: loadNumber }))
        )
        const row = pages
            .flatMap((page) => page.rows)
            .find((candidate): candidate is TmsInvoiceRow => 'status_group' in candidate && candidate.load_id === loadId)
        if (row) {
            useFreshRows.getState().put(row)
            return
        }
        const readiness = await fetchLoadReadiness(loadNumber)
        const invoiced = readiness.invoiced.rows.find((candidate) => candidate.load_id === loadId)
        if (invoiced) {
            useFreshRows.getState().put({ ...invoiced, status_group: 'invoiced' })
            return
        }
        const found = [...readiness.ready, ...readiness.needs_documents, ...amazonRowsOf(readiness.billed_through_amazon)].find(
            (candidate) => candidate.load_id === loadId
        )
        if (found) useFreshRows.getState().put(found)
    } catch {
        // The read failed: the row keeps what the chat drew, and the next change in the panel reads it again.
    }
}
